import React, { useState, useEffect, forwardRef, ForwardedRef } from 'react';
import styles from './ExtendablePanel.module.scss';
import Icon from '../images/Icon';

interface ExtendablePanelProps {
  children?: React.ReactNode;
  width?: number;
  height?: number;
  borderColor?: string;
  normalBackgroundColor?: string;
  fullscreenBackgroundColor?: string;
  fullscreenWidth?: string;
  fullscreenHeight?: string;
  logoUrl?: string;
  logoHeight?: string;
  logoWidth?: string;
  logoPosition?: 'upperLeft' | 'upperRight' | 'bottomLeft' | 'bottomRight';
  /** Côté du bouton d'agrandissement, en bas (à droite par défaut). */
  buttonSide?: 'left' | 'right';
  onStateChange?: (isFullscreen: boolean) => void;
}

const ExtendablePanel = forwardRef(({
  children,
  width,
  height,
  borderColor = "var(--color-0)",
  normalBackgroundColor = 'transparent',
  fullscreenBackgroundColor = "var(--color-6)",
  fullscreenWidth = '100vw',
  fullscreenHeight = '100vh',
  logoUrl = '',
  logoWidth = '100px',
  logoHeight = 'auto',
  logoPosition = 'upperLeft',
  buttonSide = 'right',
  onStateChange
}: ExtendablePanelProps, ref: ForwardedRef<HTMLDivElement>) => {

  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'auto'; // Restaure l'overflow à 'auto'
      window.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.body.style.overflow = 'auto'; // Assurez-vous de restaurer l'overflow lors du nettoyage
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFullscreen]);

  const toggleFullscreen = () => {
    if (onStateChange) {
      onStateChange(!isFullscreen);
    }
    setIsFullscreen(!isFullscreen);
  };

  const getLogoStyle = () => {
    switch (logoPosition) {
      case 'upperLeft':
        return { top: '10px', left: '10px' };
      case 'upperRight':
        return { top: '10px', right: '10px' };
      case 'bottomLeft':
        return { bottom: '10px', left: '10px' };
      case 'bottomRight':
        return { bottom: '10px', right: '10px' };
      default:
        return { top: '10px', left: '10px' };
    }
  };

  return (
    <>
      <div
        ref={ref}
        className={`${styles.panel} ${isFullscreen ? styles.fullscreen : styles.normal}`}
        style={{
          width: isFullscreen ? fullscreenWidth : width ? `${width}px` : '100%',
          height: isFullscreen ? fullscreenHeight : height ? `${height}px` : '100%',
          backgroundColor: isFullscreen ? fullscreenBackgroundColor : normalBackgroundColor,
          borderColor: borderColor,
          overflow: 'hidden',
        }}
      >
        {logoUrl && isFullscreen && (
          <img
            src={logoUrl}
            alt="Logo"
            style={{
              position: 'absolute',
              ...getLogoStyle(),
              width: logoWidth,
              height: logoHeight,
              zIndex: 2011,
            }}
          />
        )}
        {children || <p>Aucun contenu à afficher</p>}
        <div className={styles.iconWrapper} style={buttonSide === 'left' ? { left: '10px', right: 'auto' } : undefined}>
          <Icon
            name="fullscreen"
            onClick={toggleFullscreen}
            width={30}
            height={30}
            color="var(--color-0)"
            hoverColor="var(--color-Y)"
          />
        </div>
      </div>
      {isFullscreen && <div className={styles.overlay} />}
    </>
  );
});

export default ExtendablePanel;
